import React, { useState, useEffect } from 'react';
import { ArrowLeft, Bookmark, MessageSquare, Eye, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { forumHelpers } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import SaveButton from './SaveButton';
import LoadingSpinner from './LoadingSpinner';

interface SavedTopicsPageProps {
  onBack: () => void;
  onNavigateToTopic?: (topicId: string, categoryId: string) => void;
}

const SavedTopicsPage: React.FC<SavedTopicsPageProps> = ({ onBack, onNavigateToTopic }) => {
  const { user } = useAuth();
  const [savedTopics, setSavedTopics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadSavedTopics();
    }
  }, [user?.id]);

  const loadSavedTopics = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await forumHelpers.savedTopics.getAll();

      if (error) {
        console.error('Erro ao carregar tópicos salvos:', error);
        setError('Erro ao carregar tópicos salvos');
      } else {
        setSavedTopics(data || []);
      }
    } catch (err) {
      console.error('Erro inesperado ao carregar tópicos salvos:', err);
      setError('Erro inesperado ao carregar tópicos salvos');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicRemoved = (topicId: string) => {
    setSavedTopics(prev => prev.filter(topic => topic.id !== topicId));
  };

  if (loading) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Voltar</span>
            </button>
          </div>
          
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" text="Carregando tópicos salvos..." />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Voltar</span>
            </button>
          </div>
          
          <div className="text-center py-20">
            <div className="text-red-400 text-lg mb-4">❌</div>
            <h2 className="text-xl font-semibold text-white mb-2">Erro ao carregar</h2>
            <p className="text-gray-400">{error}</p>
            <button
              onClick={loadSavedTopics}
              className="mt-4 bg-[#0FFF55] text-[#121316] px-6 py-2 rounded-lg font-semibold hover:bg-[#0dd149] transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#121316] p-8 pt-24">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Voltar</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0FFF55]/20 rounded-lg flex items-center justify-center">
              <Bookmark className="text-[#0FFF55]" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Tópicos Salvos</h1>
              <p className="text-gray-400">
                {savedTopics.length} {savedTopics.length === 1 ? 'discussão salva' : 'discussões salvas'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {savedTopics.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Bookmark className="text-gray-400" size={32} />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Nenhum tópico salvo</h2>
            <p className="text-gray-400 mb-6">
              Quando você salvar discussões interessantes, elas aparecerão aqui
            </p>
            <button
              onClick={onBack}
              className="bg-[#0FFF55] text-[#121316] px-6 py-3 rounded-lg font-semibold hover:bg-[#0dd149] transition-colors"
            >
              Explorar discussões
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
                         {savedTopics.map((topic) => (
               <SavedTopicCard
                 key={topic.id}
                 topic={topic}
                 onRemoved={() => handleTopicRemoved(topic.id)}
                 onNavigateToTopic={onNavigateToTopic}
               />
             ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para o card de tópico salvo
interface SavedTopicCardProps {
  topic: any;
  onRemoved: () => void;
  onNavigateToTopic?: (topicId: string, categoryId: string) => void;
}

const SavedTopicCard: React.FC<SavedTopicCardProps> = ({ topic, onRemoved, onNavigateToTopic }) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      const result = await forumHelpers.savedTopics.unsave(topic.id);
      if (result.success) {
        onRemoved();
      }
    } catch (error) {
      console.error('Erro ao remover dos salvos:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleTopicClick = () => {
    if (onNavigateToTopic && topic.category_id) {
      onNavigateToTopic(topic.id, topic.category_id);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-800/80 via-gray-900/90 to-gray-800/80 backdrop-blur-xl border border-gray-700/50 shadow-2xl overflow-hidden hover:border-[#0FFF55]/30 transition-all duration-200 cursor-pointer">
      <CardContent className="p-6" onClick={handleTopicClick}>
        <div className="flex items-start gap-4">
          {/* Avatar do autor */}
          <Avatar className="w-10 h-10 flex-shrink-0">
            <AvatarImage src={topic.author_avatar_url} />
            <AvatarFallback className="bg-[#0FFF55] text-[#121316] font-semibold">
              {topic.author_full_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            {/* Header com título e badges */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white hover:text-[#0FFF55] transition-colors line-clamp-2">
                  {topic.title}
                </h3>
                
                {/* Categoria */}
                {topic.category_name && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge 
                      variant="secondary" 
                      className="text-xs"
                      style={{ 
                        backgroundColor: topic.category_color + '20',
                        color: topic.category_color,
                        borderColor: topic.category_color + '40'
                      }}
                    >
                      {topic.category_icon} {topic.category_name}
                    </Badge>
                  </div>
                )}
              </div>
              
                             {/* Botão de remover dos salvos */}
               <button
                 onClick={(e) => {
                   e.stopPropagation(); // Prevenir que o clique no botão ative o clique do card
                   handleRemove();
                 }}
                 disabled={isRemoving}
                 className="ml-4 p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                 title="Remover dos salvos"
               >
                 {isRemoving ? (
                   <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                 ) : (
                   <Bookmark className="w-4 h-4" />
                 )}
               </button>
            </div>
            
            {/* Metadados */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
              <span>{topic.author_full_name || 'Usuário'}</span>
              <span>•</span>
              <span>
                {topic.created_at && formatDistanceToNow(new Date(topic.created_at), { 
                  addSuffix: true, 
                  locale: ptBR 
                })}
              </span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                <span>{topic.reply_count || 0} respostas</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{topic.view_count || 0} visualizações</span>
              </div>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Heart className="w-4 h-4" />
                <span>{topic.votes_count || 0} likes</span>
              </div>
            </div>
            
            {/* Conteúdo */}
            <p className="text-gray-300 line-clamp-3 mb-4">
              {topic.content}
            </p>
            
            {/* Data de salvamento */}
            <div className="text-xs text-gray-500">
              Salvo em {topic.saved_at && formatDistanceToNow(new Date(topic.saved_at), { 
                addSuffix: true, 
                locale: ptBR 
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SavedTopicsPage; 